import React, { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { Theme } from "@literati/public-ui-shared-react";

import { CheckoutContext } from "contexts/CheckoutProvider";
import LoadingAnimation from "@/atoms/LoadingAnimation";
// @TODO: remove mock
import { mockBooks } from "__mocks__/mockBookData";
import { useDefaultFunnelPlan } from "utils/hooks/useDefaultFunnelPlan";

import StepLayout, { StepLayoutClassName } from "components/Step/StepLayout";
import useStep from "components/Step/useStep";
import StepFlow from "components/Step/StepFlow";
import { ReaderProfileKey } from "components/Step/stepTypes";

import { MEMBERSHIP_STEP_GET_STARTED } from "components/Membership/MembershipStepGetStarted";
import { MEMBERSHIP_STEP_SELECT_BOOKS } from "components/Membership/MembershipStepSelectBooks";
import {
  MEMBERSHIP_STEP_READER_PROFILE,
  MEMBERSHIP_STEP_READER_PROFILE_SUPPRESS_BACK_NAVIGATION,
} from "components/Membership/MembershipStepReaderProfile";
import { MEMBERSHIP_STEP_SELECT_PLAN } from "components/Membership/MembershipStepSelectPlan";
import {
  MEMBERSHIP_STEP_CHECKOUT,
  MEMBERSHIP_STEP_CHECKOUT_CREATE_ACCOUNT,
} from "components/Membership/MembershipStepCheckout";
import { MEMBERSHIP_STEP_PAYMENT } from "components/Membership/MembershipStepPayment";
import {
  MEMBERSHIP_STEP_SHIPPING,
  MEMBERSHIP_STEP_SHIPPING_SUPPRESS_BACK_BUTTON,
} from "components/Membership/MembershipStepShipping";
import { MEMBERSHIP_STEP_CONFIRMATION_PURCHASE } from "components/Membership/MembershipStepConfirmationPurchase";
import { MEMBERSHIP_STEP_READING_LEVEL } from "components/Membership/MembershipStepReadingLevel";
import {
  MEMBERSHIP_STEP_RELATIONSHIP,
  MEMBERSHIP_STEP_RELATIONSHIP_SUPPRESS_BACK_NAVIGATION,
} from "components/Membership/MembershipStepRelationship";
import { MEMBERSHIP_STEP_GENDER } from "components/Membership/MembershipStepGender";
import {
  MEMBERSHIP_STEP_THEME_PREFERENCES,
  MEMBERSHIP_STEP_THEME_PREFERENCES_GIFT,
} from "components/Membership/MembershipStepThemePreferences";
import { MEMBERSHIP_STEP_GOALS } from "components/Membership/MembershipStepGoals";
import { MEMBERSHIP_STEP_BUDGET } from "components/Membership/MembershipStepBudget";
import { MEMBERSHIP_STEP_CONFIRMATION_READING_LEVEL } from "components/Membership/MembershipStepConfirmationReadingLevel";
import { MEMBERSHIP_STEP_SELECT_PLAN_PREPAID } from "components/Membership/MembershipStepSelectPlanPrepaid";
import { MEMBERSHIP_STEP_CONFIRMATION_ONBOARDING } from "components/Membership/MembershipStepConfirmationOnboarding";
import { MEMBERSHIP_STEP_BOOK_SHUFFLE } from "components/Membership/MembershipStepBookShuffle";
import {
  ServerSidePropsMembership,
  completeGSUQuiz,
  shouldSkipBookShuffle,
  shouldSkipReadingLevelStep,
} from "components/Membership/membershipUtil";
import {
  MembershipState,
  MembershipStateUpdate,
  MembershipStep,
  MembershipSubmit,
} from "components/Membership/membershipTypes";

import {
  getSelectedPlan,
  getPlanBoxImageProps,
  getPlanPrepaidPricing,
} from "components/PlanSelection/planSelectionUtil";
import {
  PlanPrepaid,
  isPlanPrepaidSlug,
} from "components/PlanSelection/planSelectionTypes";

export const MEMBERSHIP_PURCHASE_STEPS: MembershipStep[] = [
  MEMBERSHIP_STEP_SELECT_BOOKS,
  MEMBERSHIP_STEP_GET_STARTED,
  MEMBERSHIP_STEP_READER_PROFILE,
  MEMBERSHIP_STEP_SELECT_PLAN,
  MEMBERSHIP_STEP_CHECKOUT,
  MEMBERSHIP_STEP_PAYMENT,
];

export const MEMBERSHIP_POST_PURCHASE_STEPS: MembershipStep[] = [
  MEMBERSHIP_STEP_SHIPPING_SUPPRESS_BACK_BUTTON,
  MEMBERSHIP_STEP_CONFIRMATION_PURCHASE,
];

export const MEMBERSHIP_ONBOARDING_STEPS: MembershipStep[] = [
  MEMBERSHIP_STEP_RELATIONSHIP,
  MEMBERSHIP_STEP_READING_LEVEL,
  MEMBERSHIP_STEP_GENDER,
  MEMBERSHIP_STEP_THEME_PREFERENCES,
  MEMBERSHIP_STEP_GOALS,
  MEMBERSHIP_STEP_BUDGET,
  MEMBERSHIP_STEP_CONFIRMATION_ONBOARDING,
];

export const MEMBERSHIP_ALL_STEPS: MembershipStep[] = [
  ...MEMBERSHIP_PURCHASE_STEPS,
  ...MEMBERSHIP_POST_PURCHASE_STEPS,
  ...MEMBERSHIP_ONBOARDING_STEPS,
];

export const MEMBERSHIP_STEPS_NO_ACCOUNT = MEMBERSHIP_ALL_STEPS;

export const MEMBERSHIP_STEPS_DUMMY_ACCOUNT: MembershipStep[] =
  MEMBERSHIP_ALL_STEPS.filter((step) => step.slug !== "get-started");

export const MEMBERSHIP_STEPS_AUTHENTICATED: MembershipStep[] =
  MEMBERSHIP_ALL_STEPS.filter(
    (step) => step.slug !== "get-started" && step.slug !== "checkout"
  );

/**
 * @note - initially for A/A funnel experiment
 * @see - https://app.shortcut.com/literati/epic/64890/funnel-refactor-replacement-a-a-test
 */
export const MEMBERSHIP_STEPS_SUBSCRIBE: MembershipStep[] = [
  MEMBERSHIP_STEP_RELATIONSHIP_SUPPRESS_BACK_NAVIGATION,
  MEMBERSHIP_STEP_GET_STARTED,
  MEMBERSHIP_STEP_READER_PROFILE,
  MEMBERSHIP_STEP_READING_LEVEL,
  MEMBERSHIP_STEP_GENDER,
  MEMBERSHIP_STEP_THEME_PREFERENCES,
  MEMBERSHIP_STEP_GOALS,
  MEMBERSHIP_STEP_BUDGET,
  MEMBERSHIP_STEP_CONFIRMATION_READING_LEVEL,
  MEMBERSHIP_STEP_CHECKOUT_CREATE_ACCOUNT,
  MEMBERSHIP_STEP_PAYMENT,
  MEMBERSHIP_STEP_SHIPPING_SUPPRESS_BACK_BUTTON,
  MEMBERSHIP_STEP_CONFIRMATION_PURCHASE,
  MEMBERSHIP_STEP_BOOK_SHUFFLE,
  MEMBERSHIP_STEP_CONFIRMATION_ONBOARDING,
];

export const MEMBERSHIP_STEPS_SUBSCRIBE_NO_ACCOUNT = MEMBERSHIP_STEPS_SUBSCRIBE;

export const MEMBERSHIP_STEPS_SUBSCRIBE_DUMMY_ACCOUNT: MembershipStep[] =
  MEMBERSHIP_STEPS_SUBSCRIBE.filter((step) => step.slug !== "get-started");

export const MEMBERSHIP_STEPS_SUBSCRIBE_AUTHENTICATED: MembershipStep[] =
  MEMBERSHIP_STEPS_SUBSCRIBE.filter(
    (step) => step.slug !== "get-started" && step.slug !== "create-account"
  );

/**
 * @note - initially for Prepaid Plan experiment
 */
export const MEMBERSHIP_STEPS_PREPAID: MembershipStep[] = [
  MEMBERSHIP_STEP_RELATIONSHIP_SUPPRESS_BACK_NAVIGATION,
  MEMBERSHIP_STEP_GET_STARTED,
  MEMBERSHIP_STEP_READER_PROFILE,
  MEMBERSHIP_STEP_READING_LEVEL,
  MEMBERSHIP_STEP_GENDER,
  MEMBERSHIP_STEP_THEME_PREFERENCES,
  MEMBERSHIP_STEP_BUDGET,
  MEMBERSHIP_STEP_CONFIRMATION_READING_LEVEL,
  MEMBERSHIP_STEP_SELECT_PLAN_PREPAID,
  MEMBERSHIP_STEP_CHECKOUT_CREATE_ACCOUNT,
  MEMBERSHIP_STEP_PAYMENT,
  MEMBERSHIP_STEP_SHIPPING_SUPPRESS_BACK_BUTTON,
  MEMBERSHIP_STEP_CONFIRMATION_PURCHASE,
  MEMBERSHIP_STEP_BOOK_SHUFFLE,
  MEMBERSHIP_STEP_CONFIRMATION_ONBOARDING,
];

export const MEMBERSHIP_STEPS_GIFT: MembershipStep[] = [
  MEMBERSHIP_STEP_READER_PROFILE_SUPPRESS_BACK_NAVIGATION,
  MEMBERSHIP_STEP_READING_LEVEL,
  MEMBERSHIP_STEP_SHIPPING,
  MEMBERSHIP_STEP_RELATIONSHIP,
  MEMBERSHIP_STEP_GENDER,
  MEMBERSHIP_STEP_THEME_PREFERENCES_GIFT,
  MEMBERSHIP_STEP_BOOK_SHUFFLE,
  MEMBERSHIP_STEP_CONFIRMATION_ONBOARDING,
];

const INITIAL_STATE: MembershipState = {
  // @TODO: remove mock
  bookshelfBooks: mockBooks.slice(0, 4),
  onSubmit: () => undefined,
  setState: () => undefined,
};

const StyledStepLayout = styled(StepLayout)`
  .${StepLayoutClassName.CONTENT_SPACING} {
    justify-content: center;
  }
`;

export interface MembershipStepFlowProps
  extends Omit<ServerSidePropsMembership, "orderSummary">,
    Pick<MembershipState, "orderSummary" | "excludeFromAnnual"> {
  membershipSteps: MembershipStep[];
}

export const MembershipStepFlow = ({
  membershipSteps,
  ...serverSideProps
}: MembershipStepFlowProps) => {
  const {
    checkoutSession,
    contentfulBoxImagesRaw,
    contentfulQuizModelParsed,
    contentfulQuizModelRaw,
    excludeFromAnnual,
    isAuthenticated,
    orderSummary,
    plans,
    plansPrepaid,
  } = serverSideProps;

  const [state, setState] = useState<MembershipState>({
    ...INITIAL_STATE,
    ...serverSideProps,
    selectedPlan: getSelectedPlan({
      plans,
      plansPrepaid,
      selectedPlanSlug: checkoutSession?.selectedPlan,
    }),
  });

  // sync server side state
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      checkoutSession,
      excludeFromAnnual,
      isAuthenticated,
      orderSummary,
    }));
  }, [
    checkoutSession,
    excludeFromAnnual,
    isAuthenticated,
    orderSummary,
    setState,
  ]);

  const { getCheckoutSession } = useContext(CheckoutContext);

  const setStateCallback = useCallback(
    (newState) => {
      setState((prevState) => ({ ...prevState, ...newState }));
    },
    [setState]
  );

  const useStepUtilities = useStep(membershipSteps);
  const { currentSlug, routeNextStep, routeSkipStep, nextSlug } =
    useStepUtilities;

  const { setPlan } = useDefaultFunnelPlan();

  const handleStateUpdate: MembershipStateUpdate = async (values) => {
    setState((prev) => ({ ...prev, ...values }));
  };

  const handleSubmit: MembershipSubmit = async (values) => {
    let childAge: number | undefined;

    switch (values.kind) {
      case "select-books":
        setState((prev) => ({ ...prev, selectBooks: values }));
        break;
      case "reader-profile":
        childAge = values[ReaderProfileKey.CALCULATED_AGE];
        const childName = values[ReaderProfileKey.NAME];
        setState((prev) => ({ ...prev, childAge, childName }));

        const [boxImageRes] = await Promise.allSettled([
          getPlanBoxImageProps({
            contentfulBoxImagesRaw,
            contentfulQuizModelRaw,
          }),
          getCheckoutSession?.(),
        ]);
        if (boxImageRes.status === "fulfilled") {
          setState((prev) => ({
            ...prev,
            selectPlanImageProps: boxImageRes.value,
          }));
        }
        break;
      case "select-plan":
        const selectedPlanSlug = values.planSlug;
        const selectedPlan = getSelectedPlan({
          plans,
          plansPrepaid,
          selectedPlanSlug,
        });
        if (selectedPlan) {
          if (isPlanPrepaidSlug(selectedPlanSlug)) {
            const selectedPlanPrepaidPricing = getPlanPrepaidPricing({
              plan: selectedPlan as PlanPrepaid,
              plansPrepaid,
            });
            setState?.((prev) => ({
              ...prev,
              selectedPlan,
              selectedPlanPrepaidPricing,
              // Standard Formik submit path for select-plan; marks that defaults should follow persisted plan on return.
              selectPlanChoiceCommitted: true,
            }));
          } else {
            setState?.((prev) => ({
              ...prev,
              selectedPlan,
              selectPlanChoiceCommitted: true,
            }));
          }
        }
        if (values.kind === "select-plan") {
          await getCheckoutSession?.();
        }
        break;
      case "payment":
        const { invoice, subscription, phoneNumber } = values;
        setState((prev) => ({ ...prev, invoice, subscription, phoneNumber }));
        const id = invoice?.id;
        if (id) {
          return routeNextStep?.({
            queries: {
              id: String(invoice.id),
            },
          });
        }
        break;
      case "shipping":
        const { preventRouting } = values;
        setState((prev) => ({
          ...prev,
          invoice: {
            ...prev.invoice,
            shippingAddress: {
              ...values,
              country: "US",
            },
          },
          shippingValues: values,
        }));
        if (preventRouting) {
          return;
        }
        break;
      case "relationship":
        setState((prev) => ({ ...prev, relationshipValues: values }));
        break;
      case "reading-level":
        setState((prev) => ({ ...prev, readingLevelValues: values }));
        break;
      case "gender":
        setState((prev) => ({ ...prev, genderValues: values }));
        break;
      case "reader-theme-preferences":
        setState((prev) => ({ ...prev, themePreferenceValues: values }));
        break;
      case "goals":
        setState((prev) => ({ ...prev, goalsValues: values }));
        break;
      case "budget":
        setState((prev) => ({ ...prev, budgetValues: values }));
        break;
    }

    const age = childAge ?? state.childAge;

    if (
      nextSlug === "reading-level" &&
      shouldSkipReadingLevelStep({ childAge: age })
    ) {
      return routeSkipStep?.();
    }
    if (
      nextSlug === "book-shuffle" &&
      shouldSkipBookShuffle({ childAge: age })
    ) {
      return routeSkipStep?.();
    }

    if (
      nextSlug === "reading-level-recommendation" &&
      !state.checkoutSession?.giftCode
    ) {
      const { readingLevel, readingLevelBoxImage } = await completeGSUQuiz({
        contentfulBoxImagesRaw: state.contentfulBoxImagesRaw,
        contentfulQuizModelParsed,
      });

      setState((prev) => ({
        ...prev,
        readingLevel,
        readingLevelBoxImage,
      }));
    }

    if (
      currentSlug !== "select-plan" &&
      (nextSlug === "create-account" ||
        nextSlug === "payment" ||
        nextSlug === "checkout" ||
        nextSlug === "plan")
    ) {
      if (!checkoutSession?.selectedPlan && !checkoutSession?.giftCode) {
        const selectedPlanSlug = await setPlan();
        const selectedPlan = getSelectedPlan({
          plans,
          plansPrepaid,
          selectedPlanSlug,
        });
        if (selectedPlan) {
          if (isPlanPrepaidSlug(selectedPlanSlug)) {
            const selectedPlanPrepaidPricing = getPlanPrepaidPricing({
              plan: selectedPlan as PlanPrepaid,
              plansPrepaid,
            });
            setState?.((prev) => ({
              ...prev,
              selectedPlan,
              selectedPlanPrepaidPricing,
            }));
          } else {
            setState?.((prev) => ({ ...prev, selectedPlan }));
          }
        }
      }
    }

    const id = state.invoice?.id;
    if (id) {
      return routeNextStep?.({
        queries: {
          id: String(id),
        },
      });
    }

    return routeNextStep?.();
  };

  const handlePhoneNumberChange = (phoneNumber: string) => {
    setState?.((prev) => ({ ...prev, phoneNumber }));
  };

  if (!currentSlug) {
    return (
      <StyledStepLayout asForm={false}>
        <LoadingAnimation inline />
      </StyledStepLayout>
    );
  }

  return (
    <Theme>
      <StepFlow<MembershipState>
        currentSlug={currentSlug}
        state={{
          ...state,
          ...useStepUtilities,
          onPhoneNumberChange: handlePhoneNumberChange,
          onStateUpdate: handleStateUpdate,
          onSubmit: handleSubmit,
          setState: setStateCallback,
        }}
        steps={membershipSteps}
      />
    </Theme>
  );
};

export default MembershipStepFlow;
